import { Link } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Pagination, Tag } from '@carbon/react';
import { api } from '../api/client';
import { BlockSkeleton, ErrorState, InlineError, PageHeader } from '../components/states';
import { When } from '../components/format';
import { FilterBar, useUrlFilters, type FilterField } from '../components/Filters';
import type { Game, Lenh, Nhom } from './forced/types';
import { NHOM, TRANG_THAI, TRANG_THAI_TAG, moTaKQ } from './forced/types';
import './forced/forced.scss';

// Trang chon game cua "Dat ket qua": moi game mot man rieng (/games/force/:gid, ForcedGame.tsx).
// Danh sach game lay tu he A (backend/config/danh_muc_game.json), khong viet cung o CMS.
type Data = { games: Game[] };
type LenhPage = { total: number; items: Lenh[]; creators: string[] };
const THU_TU: Nhom[] = ['mini', 'ban', 'so', 'nguoi'];
const KEYS = ['game', 'status', 'player', 'from', 'to'];

// Moi lenh, moi game: loc theo game, trang thai, nguoi choi, nguoi dat, ngay dat
function LenhList({ games }: { games: Game[] }) {
  const f = useUrlFilters(KEYS, { pageSize: 50 });
  const q = useQuery({
    queryKey: ['ep-ket-qua-lenh', f.query, f.page, f.pageSize],
    queryFn: () => api.get<LenhPage>('/api/admin/games/ep-ket-qua-lenh', { ...f.query, page: f.page, pageSize: f.pageSize }),
    placeholderData: keepPreviousData,
    refetchInterval: 5000,
  });
  const theoId = new Map(games.map((g) => [g.id, g]));
  const fields: FilterField[] = [
    { key: 'game', label: 'Game', kind: 'select', options: [['', 'Mọi game'], ...games.map((g) => [g.id, g.ten] as const)] },
    { key: 'status', label: 'Trạng thái', kind: 'select', options: [['', 'Mọi trạng thái'], ...Object.entries(TRANG_THAI)] },
    { key: 'player', label: 'Người chơi', kind: 'text', placeholder: 'Mã, tên đăng nhập hoặc nick' },
    { key: 'from', label: 'Từ ngày', kind: 'date' },
    { key: 'to', label: 'Đến ngày', kind: 'date' },
  ];
  const lenh = q.data?.items ?? [];
  return (
    <section className="panel">
      <div className="panel__head"><h2>Lệnh đặt kết quả</h2></div>
      <FilterBar id="fr" fields={fields} filters={f} />
      <InlineError error={q.error} />
      {lenh.length ? (
        <div className="fr-scroll fr-scroll--tall">
          <table className="plain fr-table">
            <thead><tr><th>#</th><th>Đặt lúc</th><th>Game</th><th>Áp cho</th><th>Kết quả đặt</th><th>Kết quả ra</th><th className="fr-r">Đã dùng</th><th>Trạng thái</th><th>Lý do</th><th>Dùng lần cuối</th></tr></thead>
            <tbody>
              {lenh.map((x) => {
                const g = theoId.get(x.game_id);
                const man = g?.man ?? 'nguoi';
                return (
                  <tr key={x.id}>
                    <td className="num muted">{x.id}</td>
                    <td><When v={x.luc} /></td>
                    <td>{g ? <Link className="cell-link" to={'/games/force/' + g.id}>{g.ten}</Link> : x.game_id}</td>
                    <td>{x.user_id != null ? <span>{x.nick ?? 'Người chơi'} <span className="muted num">#{x.user_id}</span></span> : x.phien != null ? <span>Phiên <span className="num">#{x.phien}</span></span> : <span className="muted">Hàng đợi</span>}</td>
                    <td>{moTaKQ(man, x.ket_qua)}</td>
                    <td>{x.ket_qua_ra && typeof x.ket_qua_ra === 'object' ? moTaKQ(man, x.ket_qua_ra as Record<string, unknown>) : <span className="muted">-</span>}</td>
                    <td className="fr-r num">{x.da_dung}/{x.so_lan}</td>
                    <td>
                      <Tag size="sm" type={TRANG_THAI_TAG[x.trang_thai] ?? 'gray'}>{TRANG_THAI[x.trang_thai] ?? x.trang_thai}</Tag>
                      {x.loi ? <div className="muted">{x.loi}</div> : null}
                    </td>
                    <td><span className="muted">{lyDo(x) ?? '-'}</span></td>
                    <td>{x.luc_dung ? <When v={x.luc_dung} /> : <span className="muted">-</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : <p className="muted">{q.isLoading ? 'Đang tải...' : f.active ? 'Không có lệnh khớp bộ lọc.' : 'Chưa có lệnh nào.'}</p>}
      {q.data && q.data.total > 0 ? (
        <Pagination page={f.page} pageSize={f.pageSize} pageSizes={[20, 50, 100]} totalItems={q.data.total}
          itemsPerPageText="Mỗi trang" pageRangeText={(_c, t) => `trên ${t} trang`}
          itemRangeText={(min, max, total) => `${min} đến ${max} trong ${total.toLocaleString('vi-VN')} lệnh`}
          onChange={({ page: p, pageSize: s }) => f.setPage(p, s)} />
      ) : null}
    </section>
  );
}

// ly do cu mac dinh la ten nguoi dat: khong hien ten quan tri
const lyDo = (x: Lenh) => (x.ly_do && x.ly_do !== x.tao_boi ? x.ly_do : null);

export function ForcedResultPage() {
  const q = useQuery({ queryKey: ['ep-ket-qua'], queryFn: () => api.get<Data>('/api/admin/games/ep-ket-qua'), refetchInterval: 5000 });
  if (q.isLoading) return <BlockSkeleton />;
  if (q.error || !q.data) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const { games } = q.data;

  return (
    <div className="page fr">
      <PageHeader title="Đặt kết quả" lead="Chỉ chủ sở hữu. Chọn một game để xem ván đang chạy, cược thật của người chơi, chốt kết quả ván này, xếp cầu, hoặc đặt các lượt tới cho một người chơi." />
      {THU_TU.map((nhom) => {
        const ds = games.filter((g) => g.nhom === nhom);
        if (!ds.length) return null;
        return (
          <section key={nhom}>
            <h2 className="fr-group">{NHOM[nhom]}</h2>
            <div className="fr-list">
              {ds.map((g) => (
                <Link key={g.id} className="fr-card" to={'/games/force/' + g.id}>
                  <span className="fr-card__ten">{g.ten}</span>
                  <span className="fr-card__id">{g.id}</span>
                  <span className="fr-card__foot">
                    {g.cho ? <Tag size="sm" type="blue">{g.cho} lệnh đang chờ</Tag> : null}
                    {g.loai === 'nguoi' ? <Tag size="sm" type="cool-gray">theo người chơi</Tag> : null}
                    {g.hu ? <Tag size="sm" type="purple">có hũ</Tag> : null}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        );
      })}

      <LenhList games={games} />
    </div>
  );
}
